<template>
  <!-- moved to document.body: element-ui 1.4 dialogs are position:absolute + transform, so one nested in the case modal stacks inside it -->
  <div ref="dialogContainer">
    <send-progress-note-dialog
      v-if="visible"
      :visible="visible"
      :client="noteCase.client"
      :infection-case="noteCase"
      :case-id="noteCase.id"
      :precautions-lookup="precautionsLookup"
      :initial-text="noteText"
      @closed="onClosed"
    />
  </div>
</template>
<script>
import transformer from "../../transformer";
import util from "../../util";
import { buildResolutionNoteText } from "../../services/infectionNoteText";
import { loadInfectionNoteData, loadCasePrecautions } from "../../services/infectionNoteData";
import { isPccNoteClient } from "../../services/PccProgressNoteService";
import SendProgressNoteDialog from "./SendProgressNoteDialog";

const RESOLUTION_NOTE_LOAD_ERROR = "Could not prepare the PointClickCare resolution note.";

/**
 * Owns the "Send progress note to PointClickCare" dialog for every host that resolves cases (case modal,
 * Surveillance list, outbreak detail). Hosts call startResolve() when ResolveInfectionCase opens and
 * finishResolve() on its resolution-save / resolution-close; the note is offered on a case's first resolve only.
 * Emits "sent" after a note is posted.
 */
export default {
  name: "pcc-note-sender",
  props: {
    canModify: { type: Boolean, required: true }
  },
  components: {
    "send-progress-note-dialog": SendProgressNoteDialog
  },
  data() {
    return {
      visible: false,
      noteCase: null, // legacy-model case (transformer.mapInfectionCaseToLegacyModel)
      noteText: null,
      firstResolveCaseId: null
    };
  },
  computed: {
    precautionsLookup() {
      return util.arrayToObj(this.$configStore.data.precautions || [], "id");
    }
  },
  methods: {
    /** on-demand send of an already-built note */
    open(noteCase, noteText) {
      this.noteCase = noteCase;
      this.noteText = noteText;
      this.visible = true;
    },
    /** call when the resolve dialog opens, before it changes the case */
    startResolve(infectionCase) {
      this.firstResolveCaseId = infectionCase.resolutionDate ? null : infectionCase.id;
    },
    /**
     * call on resolution-save / resolution-close with the emitted case (undefined on an early close).
     * noteCase: the resolved case in the legacy model; defaults to mapping the emitted case.
     */
    finishResolve(resolution, noteCase) {
      const firstResolve = !!resolution && resolution.id === this.firstResolveCaseId;
      this.firstResolveCaseId = null;
      if (firstResolve && resolution.resolved && this.canModify && isPccNoteClient(this.$configStore, resolution.client)) {
        this.openResolutionNote(noteCase || transformer.mapInfectionCaseToLegacyModel(JSON.parse(JSON.stringify(resolution))));
      }
    },
    async openResolutionNote(noteCase) {
      try {
        const [{ labs, treatments }, precautions] = await Promise.all([loadInfectionNoteData(this.$http, noteCase), loadCasePrecautions(this.$http, noteCase)]);
        const noteText = buildResolutionNoteText({
          client: noteCase.client,
          infectionCase: noteCase,
          labs,
          treatments,
          precautions,
          precautionsLookup: this.precautionsLookup,
          dateFormat: this.$configStore.dateFormat()
        });
        this.open(noteCase, noteText);
      } catch (error) {
        // the case is already resolved; only the note prompt is skipped
        this.$message.error(RESOLUTION_NOTE_LOAD_ERROR);
      }
    },
    onClosed(result) {
      this.visible = false;
      if (result && result.sent) {
        this.$emit("sent");
      }
    }
  },
  mounted() {
    document.body.append(this.$refs.dialogContainer);
  },
  beforeDestroy() {
    // a host being destroyed only removes its own root from the DOM, not this moved container
    this.$refs.dialogContainer.remove();
  }
};
</script>
